import React, { useRef, useState, useEffect, useCallback } from 'react'
import Cart from '../Cart'
import { Row, Col } from 'react-bootstrap'
import { connect } from 'react-redux'
import styles from './styles.less'
import HeaderNavItem from '../HeaderNavItem'
import { Link } from 'react-router-dom'
import Sidebar from '../Sidebar'
import { HEADLESS } from '../../../constants/headlessSetting'
import classnames from 'classnames'
import navItemStyles from '../HeaderNavItem/styles.less'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone, faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons'
import { menuTranslate } from 'helpers/routing'
import { changeLocale as changeLocaleAction } from '../../../actions'
import LanguageSelector from './LanguageSelector'
import { getLanguageOptions } from './languageConfig'

const setDocumentLanguage = (locale) => {
  if (typeof document === 'undefined' || !locale) return
  document.documentElement.lang = locale
}

export const Header = (props) => {
  const scrollMenuRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [pendingLocale, setPendingLocale] = useState(null)
  const currentLocale = props?.shop?.locale

  const checkScroll = () => {
    const element = scrollMenuRef.current
    if (!element) return

    setShowLeftArrow(element.scrollLeft > 0)

    const maxScrollLeft = element.scrollWidth - element.clientWidth
    setShowRightArrow(element.scrollLeft < maxScrollLeft - 5)
  }

  const scrollLeft = () => {
    scrollMenuRef.current.scrollBy({ left: -200, behavior: 'smooth' })
  }

  const scrollRight = () => {
    scrollMenuRef.current.scrollBy({ left: 200, behavior: 'smooth' })
  }

  useEffect(() => {
    const element = scrollMenuRef.current
    if (!element) return

    checkScroll()

    element.addEventListener('scroll', checkScroll)
    window.addEventListener('resize', checkScroll)

    return () => {
      element.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!currentLocale) return
    setDocumentLanguage(currentLocale)
    if (typeof window === 'undefined' || !window.localStorage) return
    try {
      window.localStorage.setItem('locale', currentLocale)
    } catch (error) {
      if (process.env.NODE_ENV === 'development')
        console.warn('Unable to persist locale', error) // eslint-disable-line no-console
    }
  }, [currentLocale])

  useEffect(() => {
    if (pendingLocale && currentLocale === pendingLocale) {
      setPendingLocale(null)
    }
  }, [pendingLocale, currentLocale])

  const handleLanguageChange = useCallback((event) => {
    const nextLocale = event?.target?.value
    if (!nextLocale) return
    if (nextLocale === currentLocale || nextLocale === pendingLocale) return

    setPendingLocale(nextLocale)
    setDocumentLanguage(nextLocale)

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('locale', nextLocale)
      } catch (error) {
        if (process.env.NODE_ENV === 'development')
          console.warn('Unable to persist locale', error) // eslint-disable-line no-console
      }
    }

    const actionResult = props.changeLocale ? props.changeLocale(nextLocale) : null
    if (actionResult && typeof actionResult.then === 'function') {
      actionResult.catch(() => {
        setPendingLocale(currentLocale || null)
        setDocumentLanguage(currentLocale)
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            if (currentLocale)
              window.localStorage.setItem('locale', currentLocale)
            else
              window.localStorage.removeItem('locale')
          } catch (error) {
            if (process.env.NODE_ENV === 'development')
              console.warn('Unable to restore locale', error) // eslint-disable-line no-console
          }
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocale, pendingLocale, props.changeLocale])

  if (props.shop.id) {
    const { menu, shop, headless } = props
    let menuLeft = menu.left, menuRight = menu.right
    if (shop.locale === 'nb') {
      menuLeft = menuTranslate(menu.left)
      menuRight = menuTranslate(menu.right)
    }

    const itemsLeft = menuLeft.map((item, index) => <HeaderNavItem key={index} item={item}/>)
    const itemsRight = menuRight.map((item, index) => <HeaderNavItem key={index} item={item}/>)

    const languageOptions = getLanguageOptions(shop)
    const effectiveLocale = pendingLocale || currentLocale
    const selectedLocale = languageOptions.find(option => option.value === effectiveLocale)
      ? effectiveLocale
      : (languageOptions[0] && languageOptions[0].value)

    const renderLanguageSelector = (id, className) => {
      if (languageOptions.length <= 1 || !selectedLocale) return null
      return (
        <LanguageSelector
          id={id}
          className={className}
          options={languageOptions}
          selectedLocale={selectedLocale}
          pendingLocale={pendingLocale}
          onSelect={handleLanguageChange}
        />
      )
    }

    const languageSelectorMenuComponent = renderLanguageSelector('header-language-select-menu', styles.languageSelector)
    const languageSelectorMenu = headless === HEADLESS.SHOW_MENU_ONLY && languageSelectorMenuComponent
      ? (
          <li className={classnames('header-nav-item', navItemStyles.navItem, styles.languageMenuItem)}>
            {languageSelectorMenuComponent}
          </li>
        )
      : null
    const languageSelectorSidebar = renderLanguageSelector('header-language-select-sidebar', styles.languageSelectorSidebar)

    let phoneNumber
    if (shop.phoneNumber)
      phoneNumber = <span><FontAwesomeIcon icon={faPhone} className={styles.phoneIcon} />{shop.phoneNumber}</span>

    if (shop.webshopId === 25)
      phoneNumber = <span><FontAwesomeIcon icon={faPhone} className={styles.phoneIcon} />040-688-76-51</span>

    const navbar = (
        <div className={classnames([styles.fullWidth, 'background-dark', 'header-navbar'])}>
          <div className={classnames([styles.navbarContainer, "container"])}>
            <div className={classnames('nav-left', styles.navLeft)}>
              <div className={`${styles.menuContainer}`}>
                {showLeftArrow && (
                  <button
                    className={`${styles.navArrow} ${styles.navArrowLeft}`}
                    onClick={scrollLeft}
                    aria-label="Scroll left"
                  >
                    <FontAwesomeIcon className={styles.arrow} icon={faCaretLeft}/>
                  </button>
                )}

                <ul className={styles.scrollMenu} ref={scrollMenuRef}>
                  {itemsLeft}
                </ul>

                {showRightArrow && (
                  <button
                    className={`${styles.navArrow} ${styles.navArrowRight}`}
                    onClick={scrollRight}
                    aria-label="Scroll right"
                  >
                    <FontAwesomeIcon className={styles.arrow} icon={faCaretRight}/>
                  </button>
                )}
              </div>
            </div>
            <div className={classnames('nav-right', styles.navRight)}>
              {languageSelectorMenu}
              {itemsRight}
              {headless === HEADLESS.SHOW_MENU_ONLY
                ? (
                    <li className={classnames('header-nav-item', navItemStyles.navItem)}>
                      <Cart className={styles.headlessCart} />
                    </li>
                  )
                : null
              }
            </div>
        </div>
      </div>
    )
    if (headless === HEADLESS.HIDE_ALL)
      return (
        <header className={classnames("container", "headless-header", styles.headlessHeader)}>
          <Row>
            <Col md="1" mdOffset="11">
              <Cart />
            </Col>
          </Row>
        </header>
      )
    else if (headless === HEADLESS.SHOW_MENU_ONLY)
      return (
        <header className={classnames('header', styles.header)}>
          {navbar}
        </header>
      )
    return (
      <header className={classnames('header', styles.header)}>
        <div className={`${styles.fullWidth} background-normal`}>
          <Row className={classnames('header-wrapper', styles.wrapperHeader)}>
            <Col md={2} xs={12} className={classnames('phone-number-container', styles.phoneNumber)}>{phoneNumber}</Col>
            <Col md={8} xs={12} className={classnames('header-container', styles.headerContainer)}>
              <Row className={styles.flexSpaceBetween}>
                <Col sm={12} className={classnames('logo-container', styles.logo)}>
                  <Link to="/" className={classnames('logo', styles.logotypeImage)}>
                    <img src={props.shop.style.logoUrl} className="logo-image" alt="/images/logotype.png"/>
                  </Link>
                </Col>
              </Row>
            </Col>
            <Col md={2} xs={12} className={classnames('sidebar-container', styles.sidebarContainer, styles.pullRight)}>
              <Sidebar
                className={classnames('sidebar', styles.sidebar)}
                options={menuRight}
                extended={menuLeft}
              />
              <div className={styles.cartLanguageWrapper}>
                {languageSelectorSidebar}
                <Cart />
              </div>
            </Col>
          </Row>
        </div>
        {navbar}
      </header>
    )
  }
}

const mapStateToProps = state => {
  return {
    shop: state.shop,
    menu: state.menu,
    headless: state.shop.headless,
  }
}

export default connect(
  mapStateToProps,
  {
    changeLocale: changeLocaleAction
  }
)(Header)
